/**
 * main index file that renders the app
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import { SplashScreen } from '@capacitor/splash-screen';
import { handleSessionOnIdleState } from './App.utils';
import { register, unregister } from './register-service-worker';
import config from './config';

// React 18+ warns about `defaultProps` on function components; some third-party
// deps (notably `@material-ui/pickers`) still use this pattern. Until we migrate
// off those deps, silence this specific warning to keep the console actionable.
// (Narrow filter: only in dev, only this message.)
if (process.env.NODE_ENV !== 'production') {
  const originalConsoleError = console.error;
  console.error = (...args) => {
    const [firstArg] = args;
    const isDefaultPropsDeprecationWarning =
      typeof firstArg === 'string' &&
      firstArg.includes('Support for defaultProps will be removed from function components') &&
      firstArg.includes('PickerWithState');

    if (isDefaultPropsDeprecationWarning) return;
    originalConsoleError(...args);
  };
}

const initAndRenderApp = async () => {
  try {
    // Only to be used in development to check for performance issues
    // Uncomment while debugging performance issues
    // if (process.env.NODE_ENV === 'development') {
    //   const whyDidYouRender = require('@welldone-software/why-did-you-render');
    //   whyDidYouRender(React, {
    //     trackAllPureComponents: true,
    //   });
    // }

    // Service worker:
    // - In production, register (used for caching + Braze in-app messaging).
    // - In development, explicitly unregister to avoid stale cached bundles causing
    //   "Attempted import error: ... not exported from react-dom" style issues.
    if (process.env.NODE_ENV === 'production') {
      await register();
    } else if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
      try {
        const didUnregisterFlag = 'dev_sw_unregistered_once';
        const didUnregisterOnce = window.sessionStorage?.getItem(didUnregisterFlag);

        if (!didUnregisterOnce) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister()));
          window.sessionStorage?.setItem(didUnregisterFlag, '1');
          // If a SW was controlling this page, a reload is needed to fully detach it.
          if (navigator.serviceWorker.controller) {
            window.location.reload();
            return;
          }
        } else {
          // Best-effort cleanup for any leftover registration.
          unregister();
        }
      } catch (e) {
        // Don't block app startup in dev.
      }
    }

    const { initNativeSecretKey } = await import('./init/native-secret-key');
    await initNativeSecretKey();
    const initPromises = [
      import('./init/appboy'),
      import('./init/capacitor'),
      import('./init/auth'), // only used for web user for refresh or url change on web.
      import('./init/logout'), // only used for native application users to logout them on app killing.
      import('./init/polyfills'),
      import('./init/globalStyles')
    ];

    // Play Integrity: conditionally load based on feature flag
    if (config.playIntegrity?.enabled) {
      initPromises.push(import('./init/playIntegrity'));
    }

    await Promise.all(initPromises);
    try {
      await SplashScreen.hide();
      const { default: logger } = await import('./utils/logger');
      logger.info('Splash screen is hidden', 'index.jsx');
      await handleSessionOnIdleState();
    } catch (error) {
      const { default: logger } = await import('./utils/logger');
      logger.error('Unable to hide splash screen', 'index.jsx', { error });
    }
  } catch (error) {
    const { default: logger } = await import('./utils/logger');
    logger.error('Error while initializing app', 'index.jsx', { error });
  } finally {
    const { default: App } = await import('./App');
    const rootElement = document.getElementById('root');
    const root = createRoot(rootElement);
    root.render(<App />);
  }
};

initAndRenderApp();
